import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchAllSharedCalendars,
  fetchSharedCalendars,
  type SharedCalendar,
} from './sharedCalendarsApi';

export function buildSharedCalendarsQueryKey(type: string) {
  return ['shared-calendars', type] as const;
}

export function useSharedCalendarsQuery({
  type = 'saved',
  enabled = true,
}: {
  type?: string;
  enabled?: boolean;
} = {}): UseQueryResult<SharedCalendar[], Error> {
  return useQuery({
    queryKey: buildSharedCalendarsQueryKey(type),
    queryFn: ({ signal }) => fetchSharedCalendars(type, signal),
    enabled,
    staleTime: 2 * 60_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Plan-authoring fetch — `GET /shared_calendars/` (index). Returns
 * every saved shared calendar in the workspace without filtering by
 * the caller's access. See `fetchAllSharedCalendars` for the why.
 *
 * Keyed under `['shared-calendars', 'all']` so it doesn't share cache
 * with the access-filtered `useSharedCalendarsQuery({type: 'saved'})`.
 */
export function useAllSharedCalendarsQuery({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): UseQueryResult<SharedCalendar[], Error> {
  return useQuery({
    queryKey: buildSharedCalendarsQueryKey('all'),
    queryFn: ({ signal }) => fetchAllSharedCalendars(signal),
    enabled,
    staleTime: 2 * 60_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
